import React, { useCallback, useEffect, useState } from "react";
import { getContentRevisions, restoreContent } from "../../../services/api/contentAdminService";
import { isAuthError, isCanceledError } from "../../../services/errors/errorHandler";
import { formatBusinessDateTime } from "../../../services/utils/dateUtils";
import { SETTINGS_ITEMS } from "./collections";
import { describeSchedule } from "./formModel";

/**
 * "Earlier versions" of an item (A.12 revisions, newest first) with
 * "Restore this version" behind a confirmation. Restoring puts that
 * version's words and its Visible setting back.
 */

/**
 * What ended a version, in words. A revision holds the item as it was until
 * the change named by its `action` replaced it (contract A.12); a `create`
 * revision is the first version itself.
 */
export const ACTION_WORDS = Object.freeze({
  update: "Then edited",
  visibility: "Then shown or hidden",
  delete: "Then deleted",
  undelete: "Then brought back",
  restore: "Then replaced by an earlier version",
});

const whoDid = (createdBy) => (createdBy === "seed" ? "the original website" : createdBy || "unknown");

/** A line that tells versions apart: its name, plus the dates for a group. */
export const summarizeVersion = (collection, slug, data = {}) => {
  if (collection === "settings") {
    if (slug === "banner") return data.text || "";
    return (SETTINGS_ITEMS[slug] && SETTINGS_ITEMS[slug].label) || "";
  }
  const name = data.title || data.question || data.caption || "";
  if (collection === "groups" && data.schedule) return `${name} · ${describeSchedule(data.schedule)}`;
  return name;
};

/**
 * @param {object} props
 * @param {object} props.item - The current item
 * @param {boolean} props.dirty - Unsaved changes on the form (restoring drops them)
 * @param {(item: object, revision: object) => void} props.onRestored
 */
export default function Revisions({ item, dirty, onRestored }) {
  const [open, setOpen] = useState(false);
  const [revisions, setRevisions] = useState(null);
  const [error, setError] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const { collection, id, updated_at: updatedAt } = item;

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setError("");
    getContentRevisions(collection, id, { signal: controller.signal })
      .then((data) => {
        if (!controller.signal.aborted) setRevisions(data.revisions || []);
      })
      .catch((err) => {
        if (controller.signal.aborted || isCanceledError(err) || isAuthError(err)) return;
        setError(err.message || "Could not load earlier versions.");
      });
    return () => controller.abort();
    // updatedAt: any save, show/hide or restore adds a version
  }, [open, collection, id, updatedAt, reloadKey]);

  const restore = useCallback(
    async (revision) => {
      setBusy(true);
      setError("");
      try {
        const restored = await restoreContent(collection, id, revision.id);
        setConfirmId(null);
        onRestored(restored, revision);
      } catch (err) {
        if (!isAuthError(err)) setError(err.message || "Could not restore that version.");
      } finally {
        setBusy(false);
      }
    },
    [collection, id, onRestored]
  );

  return (
    <section className="cms-section" aria-labelledby="cms-revisions-title">
      <h2 id="cms-revisions-title" className="cms-section__title">
        <button
          type="button"
          className="cms-disclosure"
          aria-expanded={open}
          aria-controls="cms-revisions"
          onClick={() => setOpen((o) => !o)}
        >
          Earlier versions
        </button>
      </h2>
      {open && (
        <div id="cms-revisions">
          <p className="admin-muted">
            Every change is kept. Each version shows the item as it was until the next change. Restore
            one to put its words and its Visible setting back.
          </p>
          {error && (
            <div className="admin-error" role="alert">
              <p>{error}</p>
              <button type="button" className="admin-btn" onClick={() => setReloadKey((k) => k + 1)}>Try again</button>
            </div>
          )}
          {!revisions && !error && <p className="admin-muted" role="status">Loading earlier versions…</p>}
          {revisions && revisions.length === 0 && <p className="admin-empty">No earlier versions yet.</p>}
          {revisions && revisions.length > 0 && (
            <ul className="admin-list">
              {revisions.map((rev) => (
                <li key={rev.id} className="admin-booking cms-revision" data-revision-id={rev.id}>
                  <p className="cms-revision__when">
                    {rev.action === "create" ? "First version, " : "Until "}
                    {formatBusinessDateTime(rev.created_at)}
                  </p>
                  <p className="cms-revision__meta">
                    {rev.action === "create" ? "Created" : ACTION_WORDS[rev.action] || "Then changed"} by{" "}
                    {whoDid(rev.created_by)} · {rev.is_visible ? "was on the site" : "was hidden"}
                  </p>
                  <p className="cms-revision__summary">{summarizeVersion(collection, item.slug, rev.data)}</p>
                  {confirmId === rev.id ? (
                    <div className="admin-panel" role="group" aria-label="Confirm restore">
                      <p className="admin-panel__title">Put this version back?</p>
                      <p className="admin-panel__detail">
                        It replaces what’s saved now (you can undo it here afterwards).
                        {dirty && " Changes you haven’t saved on this page will be lost."}
                      </p>
                      <div className="admin-actions">
                        <button type="button" className="admin-btn admin-btn--primary" disabled={busy} onClick={() => restore(rev)}>
                          {busy ? "Restoring…" : "Yes, restore it"}
                        </button>
                        <button type="button" className="admin-btn" disabled={busy} onClick={() => setConfirmId(null)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="admin-btn" onClick={() => setConfirmId(rev.id)}>
                      Restore this version
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
